import { useCallback, useRef, useState } from "react";
import { Reveal } from "@/components/Reveal";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Sparkle, X } from "@phosphor-icons/react";
import { streamAI, itinerariesApi } from "@/lib/apiClient";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AIMarkdown from "@/components/AIMarkdown";
import { useTranslation } from "react-i18next";

const TIME_OPTIONS_CONFIG = [
    { k: "1_hour", emoji: "⚡" },
    { k: "half_day", emoji: "⛅" },
    { k: "full_day", emoji: "🗓️" },
    { k: "weekend", emoji: "🧳" },
];

const STYLE_OPTIONS_CONFIG = [
    { k: "romantic", emoji: "💕" },
    { k: "fun", emoji: "🎉" },
    { k: "adventurous", emoji: "🧗" },
    { k: "relaxing", emoji: "🧘" },
    { k: "luxury", emoji: "💎" },
    { k: "family", emoji: "👨‍👩‍👧" },
];

const ACTIVITY_OPTIONS = [
    "restaurant", "coffee", "park", "museum", "viewpoint",
    "music", "beach", "hike", "shopping",
    "winery", "sports", "movie", "spa",
];

const PEACH = "#F4A28F";           // warm coral used across the screenshot
const PEACH_SOFT = "rgba(244,162,143,0.14)";

export default function TravelDate() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    return (
        <section id="traveldate" className="relative py-32 md:py-40 overflow-hidden" data-testid="traveldate">
            <div
                className="absolute left-0 top-1/3 w-[800px] h-[800px] rounded-full pointer-events-none"
                style={{ background: "radial-gradient(circle, rgba(244,162,143,0.06) 0%, transparent 70%)", filter: "blur(60px)" }}
            />
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                <Reveal>
                    <div className="mb-16 grid md:grid-cols-2 gap-8 items-end">
                        <div>
                            <div className="overline mb-4">{t("travelDate.overline")}</div>
                            <h2 className="font-serif text-5xl md:text-7xl text-white leading-[0.95] tracking-tighter">
                                Love, but with a <span style={{ fontStyle: "italic", color: "var(--gold-pale)" }}>{t("travelDate.flightPlan")}</span>.
                            </h2>
                        </div>
                        <p className="text-white/60 text-lg leading-relaxed md:pb-4">
                            {t("travelDate.heroDesc")}
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.1}>
                    <div className="grid lg:grid-cols-[1.05fr_1fr] gap-8 items-start">
                        <div className="relative rounded-3xl overflow-hidden aspect-[5/6]">
                            <img
                                src="https://images.pexels.com/photos/13114680/pexels-photo-13114680.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=940"
                                alt="Romantic evening"
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 30%, rgba(11,19,32,0.75) 100%)" }} />
                            <div className="absolute bottom-0 left-0 right-0 p-8">
                                <div className="flex items-center gap-2 mb-3">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Heart key={i} size={18} weight="fill" color={PEACH} />
                                    ))}
                                </div>
                                <div className="overline text-white/70 mb-1">{t("travelDate.overline")}</div>
                                <div className="font-serif text-3xl md:text-4xl text-white">{t("travelDate.cardTitle")}</div>
                                <div className="text-white/60 text-sm mt-2">{t("travelDate.cardDesc")}</div>
                            </div>
                        </div>

                        <div className="rounded-3xl glass p-8 md:p-10 flex flex-col justify-center h-full min-h-[300px]">
                            <div className="overline mb-4">{t("travelDate.readyOverline")}</div>
                            <div className="font-serif text-3xl text-white leading-tight mb-4">
                                Set the scene. <span style={{ color: PEACH, fontStyle: "italic" }}>{t("travelDate.craftDatePart")}</span>
                            </div>
                            <p className="text-white/60 text-sm mb-8">
                                {t("travelDate.readyDesc")}
                            </p>
                            <button
                                onClick={() => setOpen(true)}
                                data-testid="traveldate-open"
                                className="w-full justify-center inline-flex items-center gap-2 py-4 rounded-full font-semibold text-base"
                                style={{ background: PEACH, color: "#1a0e0a" }}
                            >
                                <Heart size={18} weight="fill" /> {t("travelDate.planButton")}
                            </button>
                        </div>
                    </div>
                </Reveal>
            </div>

            <TravelDateModal open={open} onClose={() => setOpen(false)} />
        </section>
    );
}

export function TravelDateModal({ open, onClose }) {
    const { t } = useTranslation();
    const [location, setLocation] = useState("");
    const [budget, setBudget] = useState(100);
    const [setting, setSetting] = useState("outdoor");
    const [time, setTime] = useState("half_day");
    const [style, setStyle] = useState("romantic");
    const [activities, setActivities] = useState(["restaurant", "coffee", "viewpoint"]);
    const [notes, setNotes] = useState("");
    const [out, setOut] = useState("");
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(null);       // saved itinerary doc
    const [shared, setShared] = useState(null);     // shared post doc
    const [saving, setSaving] = useState(false);
    const outputRef = useRef(null);

    const toggleActivity = (a) => {
        setActivities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));
    };

    const craft = async () => {
        if (!location.trim()) return toast.error(t("travelDate.toasts.addLocation"));
        setLoading(true);
        setOut("");
        setSaved(null);
        setShared(null);
        try {
            const s = streamAI("/ai/craft-date", {
                location: location.trim(),
                budget_usd: Number(budget) || 100,
                setting,
                time_available: time,
                date_style: style,
                activities,
                partner_notes: notes.trim() || null,
            });
            // Scroll to output on the next tick so the panel is visible
            setTimeout(() => outputRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
            for await (const chunk of s) setOut((prev) => prev + chunk);
        } catch {
            toast.error(t("travelDate.toasts.conciergeFailed"));
        } finally {
            setLoading(false);
        }
    };

    const DUR_LABEL = {
        "1_hour": t("travelDate.modal.timeOptions.1_hour"),
        "half_day": t("travelDate.modal.timeOptions.half_day"),
        "full_day": t("travelDate.modal.timeOptions.full_day"),
        "weekend": t("travelDate.modal.timeOptions.weekend")
    };

    const saveAndShare = async () => {
        if (!out.trim()) return toast.error(t("travelDate.toasts.craftFirst"));
        setSaving(true);
        try {
            const itinerary = saved || (await itinerariesApi.save({
                kind: "date",
                destination: location.trim(),
                duration_label: DUR_LABEL[time],
                budget_usd: Number(budget) || 100,
                setting,
                style,
                activities,
                content: out,
            }));
            setSaved(itinerary);
            const { post } = await itinerariesApi.share(itinerary.id, { privacy: "public" });
            setShared(post);
            toast.success(t("travelDate.toasts.posted"));
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("travelDate.toasts.shareFailed"));
        } finally {
            setSaving(false);
        }
    };

    const justSave = async () => {
        if (!out.trim()) return toast.error(t("travelDate.toasts.craftFirst"));
        setSaving(true);
        try {
            const it = await itinerariesApi.save({
                kind: "date",
                destination: location.trim(),
                duration_label: DUR_LABEL[time],
                budget_usd: Number(budget) || 100,
                setting,
                style,
                activities,
                content: out,
            });
            setSaved(it);
            toast.success(t("travelDate.toasts.saved"));
        } catch { toast.error(t("travelDate.toasts.saveFailed")); }
        finally { setSaving(false); }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[95] flex items-stretch md:items-center justify-center md:p-6"
                    style={{ background: "rgba(3,7,15,0.9)", backdropFilter: "blur(12px)" }}
                    onClick={onClose}
                    data-testid="traveldate-modal"
                >
                    <motion.div
                        onClick={(e) => e.stopPropagation()}
                        initial={{ y: 20, opacity: 0, scale: 0.98 }} animate={{ y: 0, opacity: 1, scale: 1 }}
                        className="w-full md:max-w-lg md:rounded-3xl overflow-hidden flex flex-col md:max-h-[92vh]"
                        style={{ background: "#050B14", border: "1px solid rgba(255,255,255,0.08)" }}
                    >
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4 flex items-start gap-4 border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                            <button onClick={onClose} className="w-9 h-9 rounded-full grid place-items-center shrink-0" style={{ background: "rgba(255,255,255,0.08)" }} aria-label={t("common.close")} data-testid="traveldate-close">
                                <X size={14} />
                            </button>
                            <div>
                                <div className="overline mb-1" style={{ color: PEACH }}>{t("travelDate.overline")}</div>
                                <h3 className="font-serif text-3xl text-white leading-tight">{t("travelDate.modal.title")}</h3>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto flex-1 space-y-7">
                            {/* Location */}
                            <div>
                                <SectionLabel emoji="📍" text={t("travelDate.modal.locationLabel")} />
                                <input
                                    value={location}
                                    onChange={(e) => setLocation(e.target.value)}
                                    placeholder={t("travelDate.modal.locationPlaceholder")}
                                    className="w-full rounded-2xl px-4 py-3.5 text-base text-white focus:outline-none"
                                    style={{ background: "#0F1622", border: "1px solid rgba(255,255,255,0.08)" }}
                                    data-testid="traveldate-location"
                                />
                            </div>

                            {/* Budget + Setting */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <SectionLabel emoji="💵" text={t("travelDate.modal.budgetLabel")} />
                                    <input
                                        type="number"
                                        min={0}
                                        value={budget}
                                        onChange={(e) => setBudget(e.target.value)}
                                        className="w-full rounded-2xl px-4 py-3.5 text-base text-white focus:outline-none"
                                        style={{ background: "#0F1622", border: "1px solid rgba(255,255,255,0.08)" }}
                                        data-testid="traveldate-budget"
                                    />
                                </div>
                                <div>
                                    <SectionLabel emoji="☀️" text={t("travelDate.modal.settingLabel")} />
                                    <div className="flex gap-2">
                                        {["outdoor", "indoor"].map((k) => (
                                            <Chip
                                                key={k}
                                                selected={setting === k}
                                                onClick={() => setSetting(k)}
                                                testId={`traveldate-setting-${k}`}
                                            >
                                                {t(`travelDate.modal.${k}`)}
                                            </Chip>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Time */}
                            <div>
                                <SectionLabel emoji="⏰" text={t("travelDate.modal.timeLabel")} />
                                <div className="flex flex-wrap gap-2">
                                    {TIME_OPTIONS_CONFIG.map((tOpt) => (
                                        <Chip key={tOpt.k} selected={time === tOpt.k} onClick={() => setTime(tOpt.k)} testId={`traveldate-time-${tOpt.k}`}>
                                            <span className="mr-1.5">{tOpt.emoji}</span>{t(`travelDate.modal.timeOptions.${tOpt.k}`)}
                                        </Chip>
                                    ))}
                                </div>
                            </div>

                            {/* Date style */}
                            <div>
                                <SectionLabel emoji="💕" text={t("travelDate.modal.styleLabel")} />
                                <div className="flex flex-wrap gap-2">
                                    {STYLE_OPTIONS_CONFIG.map((sOpt) => (
                                        <Chip key={sOpt.k} selected={style === sOpt.k} onClick={() => setStyle(sOpt.k)} testId={`traveldate-style-${sOpt.k}`}>
                                            <span className="mr-1.5">{sOpt.emoji}</span>{t(`travelDate.modal.styleOptions.${sOpt.k}`)}
                                        </Chip>
                                    ))}
                                </div>
                            </div>

                            {/* Activities */}
                            <div>
                                <SectionLabel emoji="🍽️" text={t("travelDate.modal.activitiesLabel")} />
                                <div className="flex flex-wrap gap-2">
                                    {ACTIVITY_OPTIONS.map((a) => (
                                        <Chip
                                            key={a}
                                            selected={activities.includes(a)}
                                            onClick={() => toggleActivity(a)}
                                            testId={`traveldate-activity-${a}`}
                                        >
                                            {t(`travelDate.modal.activitiesOptions.${a}`, a)}
                                        </Chip>
                                    ))}
                                </div>
                            </div>

                            {/* Notes */}
                            <div>
                                <SectionLabel emoji="✨" text={t("travelDate.modal.notesLabel")} />
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={2}
                                    placeholder={t("travelDate.modal.notesPlaceholder")}
                                    className="w-full rounded-2xl px-4 py-3 text-sm text-white focus:outline-none"
                                    style={{ background: "#0F1622", border: "1px solid rgba(255,255,255,0.08)" }}
                                    data-testid="traveldate-notes"
                                />
                            </div>

                            {/* Streamed output */}
                            <AnimatePresence>
                                {(out || loading) && (
                                    <motion.div
                                        ref={outputRef}
                                        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                        className="rounded-2xl p-5"
                                        style={{ background: PEACH_SOFT, border: `1px solid ${PEACH}55` }}
                                        data-testid="traveldate-output"
                                    >
                                        <div className="overline mb-3 flex items-center gap-2" style={{ color: PEACH }}>
                                            <Sparkle size={12} weight="fill" /> {t("travelDate.modal.yourDate")}
                                            {loading && (
                                                <span className="ml-auto flex items-center gap-1.5 text-[10px] tracking-widest">
                                                    <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: PEACH }} />
                                                    {t("travelDate.modal.composingState")}
                                                </span>
                                            )}
                                        </div>
                                        {out ? (
                                            <AIMarkdown className="text-sm">{out}</AIMarkdown>
                                        ) : (
                                            <div className="space-y-2">
                                                <div className="text-white/70 text-sm italic">
                                                    {t("travelDate.modal.scoutingNotice")}
                                                </div>
                                                <div className="flex gap-1.5 mt-3">
                                                    {[0, 1, 2].map((i) => (
                                                        <motion.span
                                                            key={i}
                                                            className="inline-block w-2 h-2 rounded-full"
                                                            style={{ background: PEACH }}
                                                            animate={{ opacity: [0.3, 1, 0.3] }}
                                                            transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.2 }}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Footer CTA */}
                        <div className="p-4 border-t sticky bottom-0 space-y-2" style={{ background: "#050B14", borderColor: "rgba(255,255,255,0.06)" }}>
                            {out && !loading && !shared && (
                                <div className="flex gap-2">
                                    <button
                                        onClick={justSave}
                                        disabled={saving}
                                        className="flex-1 py-3 rounded-full font-medium text-sm border transition-colors disabled:opacity-60"
                                        style={{ background: "rgba(255,255,255,0.03)", color: "white", borderColor: "rgba(255,255,255,0.14)" }}
                                        data-testid="traveldate-save"
                                    >
                                        {saving ? t("travelDate.modal.saving") : t("travelDate.modal.save")}
                                    </button>
                                    <button
                                        onClick={saveAndShare}
                                        disabled={saving}
                                        className="flex-1 py-3 rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                                        style={{ background: PEACH, color: "#1a0e0a" }}
                                        data-testid="traveldate-share"
                                    >
                                        <Sparkle size={13} weight="fill" /> {t("travelDate.modal.saveAndShare")}
                                    </button>
                                </div>
                            )}
                            {shared && (
                                <Link
                                    to={`/p/${shared.share_slug}`}
                                    className="w-full block py-3 rounded-full font-semibold text-sm text-center"
                                    style={{ background: "rgba(212,175,55,0.16)", color: "var(--gold)", border: "1px solid rgba(212,175,55,0.4)" }}
                                    data-testid="traveldate-shared-link"
                                >
                                    {t("travelDate.modal.postedToFeed")}
                                </Link>
                            )}
                            <button
                                onClick={craft}
                                disabled={loading}
                                className="w-full py-4 rounded-full font-semibold text-base flex items-center justify-center gap-2 disabled:opacity-60"
                                style={{ background: "#E4C46D", color: "#1a0e0a" }}
                                data-testid="traveldate-craft"
                            >
                                <Heart size={18} weight="fill" />
                                {loading ? t("travelDate.modal.composing") : out ? t("travelDate.modal.composeAgain") : t("travelDate.modal.craftMyDate")}
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

function SectionLabel({ emoji, text }) {
    return (
        <div className="flex items-center gap-2 mb-3">
            <span className="text-base">{emoji}</span>
            <span className="text-xs tracking-[0.2em] uppercase" style={{ color: "rgba(255,255,255,0.55)" }}>{text}</span>
        </div>
    );
}

function Chip({ selected, onClick, children, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className="px-4 py-2 rounded-full text-sm transition-all duration-200"
            style={{
                background: selected ? PEACH : "rgba(255,255,255,0.04)",
                color: selected ? "#1a0e0a" : "rgba(255,255,255,0.85)",
                border: selected ? `1px solid ${PEACH}` : "1px solid rgba(255,255,255,0.08)",
                fontWeight: selected ? 600 : 400,
            }}
        >
            {children}
        </button>
    );
}
